<script setup>
// Astra DS · ArtCarousel — el arte que pasa solo.
//
// La puerta de nuevas realizaciones enseña los cuatro últimos lanzamientos sin
// flechas ni puntos: «el propio movimiento es el aviso de que hay más»
// (prototipo, § carrusel de lanzamientos).
//
// El bucle no rebobina. Se pinta una diapositiva de más —copia de la primera— y
// al llegar a ella, terminada la transición, se quita la transición y se vuelve
// a cero. Rebobinar cuatro pasos hacia atrás delataría el truco y además dice
// lo contrario de lo que pasa: los lanzamientos avanzan, no retroceden.
//
// Emite `change` con el índice real para que quien lo aloja cambie su texto: en
// la puerta, el título y el pie son del lanzamiento que se ve.
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  images: { type: Array, default: () => [] },
  interval: { type: Number, default: 4800 },
  // Debe coincidir con la transición del CSS de abajo: el salto se hace cuando
  // termina, y adelantarse lo deja a la vista.
  duration: { type: Number, default: 640 },
});

const emit = defineEmits(["change"]);

const step = ref(0);
const jumping = ref(false);
let timer = null;
let jumpTimer = null;

const count = computed(() => props.images.length);

// La copia de la primera al final. Con una sola imagen no hay nada que pasar.
const slides = computed(() =>
  count.value > 1 ? [...props.images, props.images[0]] : props.images,
);

const width = computed(() => `${slides.value.length * 100}%`);
const offset = computed(() => `-${(100 / slides.value.length) * step.value}%`);
const slideWidth = computed(() => `${100 / slides.value.length}%`);

function stop() {
  if (timer) clearInterval(timer);
  if (jumpTimer) clearTimeout(jumpTimer);
  timer = jumpTimer = null;
}

function start() {
  stop();
  if (count.value < 2) return;

  // Quien pide no animar, no ve animación: se queda en la primera.
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  timer = setInterval(() => {
    jumping.value = false;
    step.value += 1;

    if (step.value === count.value) {
      // Ya se ve la copia; el índice real ha vuelto a cero.
      emit("change", 0);
      jumpTimer = setTimeout(() => {
        jumping.value = true;
        step.value = 0;
      }, props.duration);
      return;
    }

    emit("change", step.value);
  }, props.interval);
}

// Si la lista llega tarde —siempre llega tarde, viene de la red— el bucle
// arranca cuando hay algo que pasar, no al montar.
watch(count, () => {
  step.value = 0;
  jumping.value = false;
  emit("change", 0);
  start();
});

onMounted(start);
onUnmounted(stop);
</script>

<template>
  <div class="art-carousel">
    <div
      class="art-carousel__rail"
      :class="{ 'is-jumping': jumping }"
      :style="{ width, transform: `translateX(${offset})` }"
    >
      <span
        v-for="(src, i) in slides"
        :key="`${src}-${i}`"
        class="art-carousel__slide"
        :style="{ width: slideWidth, backgroundImage: src ? `url(${src})` : null }"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.art-carousel {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--color-art-mid);
}

.art-carousel__rail {
  display: flex;
  height: 100%;
  transition: transform 0.62s cubic-bezier(0.72, 0, 0.18, 1);

  // El salto al principio del bucle: sin transición, o se vería rebobinar.
  &.is-jumping {
    transition: none;
  }
}

// Gris por defecto, color al pasar: la misma regla que `DuotoneArt`, y por el
// mismo mecanismo. **El carrusel no decide cuándo vuelve el color**: lo anuncia
// la puerta que lo contiene poniendo `--art-filtro` (ver `_interaccion.scss`),
// así el color vuelve desde cualquier punto de la puerta y no solo desde la
// tira de imágenes.
.art-carousel__slide {
  flex: none;
  height: 100%;
  // Arriba, como el resto de los huecos del sistema: son portadas y fotos, y
  // el motivo está en la parte de arriba.
  background-position: top center;
  background-size: cover;
  filter: var(--art-filtro, grayscale(1) contrast(1.15));
  transition: filter 0.15s linear;
}

@media (prefers-reduced-motion: reduce) {
  .art-carousel__rail,
  .art-carousel__slide {
    transition: none;
  }
}
</style>
